import { useState } from "react";
import type { ApiClient } from "../../api/client";
import { useCartStore } from "../../store/cart-store";
import { sendTrackingEvent } from "../../lib/tracking";
import {
  createCustomerInfoSchema,
  type CustomerInfoSchemaType,
} from "../../schemas/customer-info.schema";
import { StripeElementsFormLazy as StripeElementsForm } from "../BookingForm/StripeElementsFormLazy";
import { useTranslations } from "../../i18n";

type CartCheckoutFormProps = {
  apiClient: ApiClient;
  cartId: string;
  onSuccess?: (cartId: string, cartNumber: string) => void;
  onError?: (error: Error) => void;
};

export function CartCheckoutForm({
  apiClient,
  cartId,
  onSuccess,
  onError,
}: CartCheckoutFormProps) {
  const {
    cart,
    currentStep,
    customerInfo,
    setCustomerInfo,
    setCurrentStep,
    bookingIds,
    clientSecret,
    setClientSecret,
    stripePublishableKey,
    setStripePublishableKey,
    cartNumber,
    setCartNumber,
    setBookingIds,
    isSubmitting,
    setSubmitting,
    isRequestBooking,
    setIsRequestBooking,
  } = useCartStore();

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const { locale, t } = useTranslations();

  const formatPrice = (priceInCents: number) => {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
    }).format(priceInCents / 100);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});

    // Construire les tickets à partir des bookingItems du panier
    // Le backend exige au moins 1 ticket dans le schema de validation
    const tickets: Record<string, number> = {};
    if (cart) {
      for (const booking of cart.bookings) {
        for (const item of booking.bookingItems) {
          tickets[item.id] = (tickets[item.id] || 0) + item.quantity;
        }
      }
    }

    const formData: CustomerInfoSchemaType = {
      firstName: customerInfo.firstName || "",
      lastName: customerInfo.lastName || "",
      email: customerInfo.email || "",
      phone: customerInfo.phone || "",
      cgv: customerInfo.cgv || false,
      isCgvPresta: "false",
      cgvPresta: false,
      tickets,
    };

    const validation = createCustomerInfoSchema(t).safeParse(formData);

    if (!validation.success) {
      const errors: Record<string, string> = {};
      validation.error.issues.forEach((err) => {
        if (err.path.length > 0) {
          const key = String(err.path[0]);
          errors[key] = err.message;
        }
      });
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const checkoutResult = await apiClient.checkoutCart(cartId, formData);

      if (!checkoutResult.success) {
        throw new Error("Checkout failed");
      }

      setCartNumber(checkoutResult.cartNumber);

      // Tracking add_shipping_info (informations client soumises)
      const activeBookings =
        cart?.bookings.filter((b) => b.status === "IN_CART") || [];
      const totalValue = activeBookings.reduce(
        (acc, b) => acc + b.itemsTotal,
        0,
      );
      const trackingItems = activeBookings.flatMap((b) =>
        b.bookingItems.map((bi) => ({
          item_id: b.id,
          item_name: b.productTitle,
          item_category: b.optionName,
          item_category2: bi.unitName,
          item_variant: b.eventDate || undefined,
          price:
            ((bi.promotionPrice ?? 0) > 0
              ? (bi.promotionPrice ?? 0)
              : (bi.price ?? 0)) / 100,
          quantity: bi.quantity,
        })),
      );

      sendTrackingEvent("add_shipping_info", "cart_shipping_info", {
        currency: "EUR",
        value: totalValue / 100,
        cart_id: cartId,
        items: trackingItems,
      });
      // Détecter si au moins une réservation est de type REQUEST (et pas un cadeau)
      const hasRequestBooking =
        cart?.bookings.some((b) => b.optionType === "REQUEST" && !b.isGift) ??
        false;
      setIsRequestBooking(hasRequestBooking);

      // Gratuit ou redirection
      if (checkoutResult.redirectUrl) {
        // Sauvegarder les IDs des bookings pour l'écran de succès
        if (cart) {
          setBookingIds(cart.bookings.map((b) => b.id));
        }
        setCurrentStep("success");
        onSuccess?.(cartId, checkoutResult.cartNumber);
        return;
      }

      // Paiement Stripe
      if (checkoutResult.clientSecret) {
        setClientSecret(checkoutResult.clientSecret);
        setStripePublishableKey(checkoutResult.stripePublishableKey || "");
        setCurrentStep("payment");
      }
    } catch (error) {
      const err =
        error instanceof Error ? error : new Error(t.cart.errorCheckout);
      onError?.(err);
      setFormErrors({ _global: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = () => {
    // Sauvegarder les IDs des bookings pour l'écran de succès
    if (cart) {
      setBookingIds(cart.bookings.map((b) => b.id));
    }
    setCurrentStep("success");
    onSuccess?.(cartId, cartNumber || "");
  };

  const handlePaymentError = (error: Error) => {
    onError?.(error);
  };

  // Étape info facturation
  if (currentStep === "info") {
    return (
      <div className="flex flex-col gap-4">
        {/* Retour au panier */}
        <button
          type="button"
          onClick={() => setCurrentStep("cart")}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors self-start"
        >
          <svg
            className="size-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5 8.25 12l7.5-7.5"
            />
          </svg>
          {t.cart.backToCart}
        </button>

        {/* Récap montant */}
        <div className="rounded-xl bg-brand-50 px-4 py-3 border border-brand-100 flex items-center justify-between">
          <span className="text-sm font-medium text-brand-800">
            {t.cart.orderTotal}
          </span>
          <span className="text-lg font-bold text-brand-700">
            {formatPrice(cart?.totalAmount || 0)}
          </span>
        </div>

        <form
          onSubmit={handleFormSubmit}
          className="flex flex-col gap-3 sm:gap-4"
        >
          <div className="flex items-center gap-2 px-1 border-l-4 border-brand-600 pl-3">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900">
              {t.cart.billingInfo}
            </h3>
          </div>

          {formErrors._global && (
            <div className="rounded-lg bg-red-50 p-2.5 sm:p-3 text-xs sm:text-sm text-red-600 border border-red-100">
              {formErrors._global}
            </div>
          )}

          <div className="space-y-2 sm:space-y-3">
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div>
                <input
                  type="text"
                  placeholder={t.form.firstName}
                  value={customerInfo.firstName || ""}
                  onChange={(e) =>
                    setCustomerInfo({ firstName: e.target.value })
                  }
                  className={`w-full rounded-lg border px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base text-gray-900 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all ${formErrors.firstName ? "border-red-300 focus:border-red-500" : "border-gray-300 focus:border-brand-500"}`}
                />
                {formErrors.firstName && (
                  <p className="mt-1 text-xs text-red-600">
                    {formErrors.firstName}
                  </p>
                )}
              </div>

              <div>
                <input
                  type="text"
                  placeholder={t.form.lastName}
                  value={customerInfo.lastName || ""}
                  onChange={(e) =>
                    setCustomerInfo({ lastName: e.target.value })
                  }
                  className={`w-full rounded-lg border px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base text-gray-900 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all ${formErrors.lastName ? "border-red-300 focus:border-red-500" : "border-gray-300 focus:border-brand-500"}`}
                />
                {formErrors.lastName && (
                  <p className="mt-1 text-xs text-red-600">
                    {formErrors.lastName}
                  </p>
                )}
              </div>
            </div>

            <div>
              <input
                type="email"
                placeholder={t.form.email}
                value={customerInfo.email || ""}
                onChange={(e) => setCustomerInfo({ email: e.target.value })}
                className={`w-full rounded-lg border px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base text-gray-900 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all ${formErrors.email ? "border-red-300 focus:border-red-500" : "border-gray-300 focus:border-brand-500"}`}
              />
              {formErrors.email && (
                <p className="mt-1 text-xs text-red-600">{formErrors.email}</p>
              )}
            </div>

            <div>
              <input
                type="tel"
                placeholder={t.form.phone}
                value={customerInfo.phone || ""}
                onChange={(e) => setCustomerInfo({ phone: e.target.value })}
                className={`w-full rounded-lg border px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base text-gray-900 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all ${formErrors.phone ? "border-red-300 focus:border-red-500" : "border-gray-300 focus:border-brand-500"}`}
              />
              {formErrors.phone && (
                <p className="mt-1 text-xs text-red-600">{formErrors.phone}</p>
              )}
            </div>
          </div>

          {/* CGV */}
          <div className="space-y-2 rounded-lg bg-gray-50 p-3 border border-gray-100">
            <label className="flex items-start gap-3 cursor-pointer">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={customerInfo.cgv || false}
                  onChange={(e) => {
                    setCustomerInfo({
                      cgv: e.target.checked,
                      cgvPresta: e.target.checked,
                    });
                  }}
                  className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-gray-300 bg-white checked:border-brand-600 checked:bg-brand-600 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                />
                <svg
                  className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100"
                  width="10"
                  height="8"
                  viewBox="0 0 10 8"
                  fill="none"
                >
                  <path
                    d="M1 4L3.5 6.5L9 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="text-xs text-gray-700">
                {t.form.acceptCgv}{" "}
                <a
                  href={`${process.env.PUBLIC_STORAGE_URL}/files/20250413_CGV_BONJOUR.FUN.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-brand-600 hover:underline"
                >
                  {t.form.cgvPlatform}
                </a>{" "}
                *
              </span>
            </label>
            {(formErrors.cgv || formErrors.cgvPresta) && (
              <p className="text-xs text-red-600 pl-7">
                {formErrors.cgv || formErrors.cgvPresta}
              </p>
            )}
          </div>

          <div className="flex pt-1 sm:pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-[2] rounded-xl bg-brand-600 px-4 py-2.5 sm:py-3 text-sm sm:text-base font-bold text-white shadow-lg shadow-brand-200 transition-all hover:bg-brand-700 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-wait"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin size-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t.common.processing}
                </span>
              ) : (
                t.cart.goToPayment.replace(
                  "{price}",
                  formatPrice(cart?.totalAmount || 0),
                )
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Étape paiement Stripe
  if (currentStep === "payment" && clientSecret && stripePublishableKey) {
    return (
      <div className="flex flex-col gap-4">
        {/* Retour */}
        <button
          type="button"
          onClick={() => setCurrentStep("info")}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors self-start"
        >
          <svg
            className="size-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5 8.25 12l7.5-7.5"
            />
          </svg>
          {t.common.back}
        </button>

        <div className="flex items-center gap-2 px-1 border-l-4 border-brand-600 pl-3">
          <h3 className="text-sm sm:text-base font-semibold text-gray-900">
            {t.cart.securePayment}
          </h3>
          <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
            <svg
              className="size-3.5 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
              />
            </svg>
            <span className="font-medium">{t.common.secure}</span>
          </div>
        </div>

        <StripeElementsForm
          stripePublishableKey={stripePublishableKey}
          clientSecret={clientSecret}
          bookingIds={bookingIds || []}
          isRequestBooking={isRequestBooking}
          trackingData={(() => {
            const activeBookings =
              cart?.bookings.filter((b) => b.status === "IN_CART") || [];
            const totalValue = activeBookings.reduce(
              (acc, b) => acc + b.itemsTotal,
              0,
            );
            return {
              cartId,
              currency: "EUR",
              value: totalValue / 100,
              items: activeBookings.flatMap((b) =>
                b.bookingItems.map((bi) => ({
                  item_id: b.id,
                  item_name: b.productTitle,
                  item_category: b.optionName,
                  item_category2: bi.unitName,
                  item_variant: b.eventDate || undefined,
                  price:
                    ((bi.promotionPrice ?? 0) > 0
                      ? (bi.promotionPrice ?? 0)
                      : (bi.price ?? 0)) / 100,
                  quantity: bi.quantity,
                })),
              ),
            };
          })()}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
        />
      </div>
    );
  }

  return null;
}
